import React, { useState, useMemo } from 'react';
import LayoutHome from '../../components/layout/LayoutHome';
import {
    Input, Button, Table, Tag, Typography, Row, Col, Modal, Form,
    InputNumber, Space, message, Divider, Grid, Card, List, Select, Popconfirm, Tooltip, Radio
} from 'antd';
import {
    SearchOutlined, EditOutlined, PlusOutlined, DeleteOutlined,
    DollarOutlined, TeamOutlined, PhoneOutlined, MailOutlined,
    CheckCircleOutlined, EnvironmentOutlined, WalletOutlined, HistoryOutlined
} from '@ant-design/icons';
// 1. IMPORTAMOS EL HOOK DEL HISTORIAL
import { useGetProveedores, useSaveProveedor, useSavePagoProveedor, useDeleteProveedor, useGetPagosProveedor } from '../../hooks/useProveedores';
import { useCaja } from '../../hooks/useCaja';
import dayjs from 'dayjs'; // 2. IMPORTAMOS DAYJS PARA LAS FECHAS
import { usePermission } from '../../hooks/usePermission';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Proveedores = () => {

    const { hasPermission } = usePermission();
    const screens = useBreakpoint();
    const isMobile = screens.md === false;

    const { getCajaActiva } = useCaja();
    const cajaActiva = getCajaActiva.data;

    const { data: proveedoresDB = [], isLoading, isFetching } = useGetProveedores();
    const saveProveedor = useSaveProveedor();
    const savePago = useSavePagoProveedor();
    const deleteProveedor = useDeleteProveedor();

    const [busqueda, setBusqueda] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProveedor, setEditingProveedor] = useState(null);
    const [form] = Form.useForm();

    const [isPagoModalOpen, setIsPagoModalOpen] = useState(false);
    const [proveedorAPagar, setProveedorAPagar] = useState(null);
    const [tipoPago, setTipoPago] = useState('total');
    const [formPago] = Form.useForm();

    // 3. ESTADOS PARA EL HISTORIAL
    const [isHistorialModalOpen, setIsHistorialModalOpen] = useState(false);
    const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
    
    // Disparamos el hook. Solo buscará datos si proveedorSeleccionado.id existe.
    const { data: historialPagos = [], isLoading: cargandoHistorial } = useGetPagosProveedor(proveedorSeleccionado?.id);

    // CÁLCULOS
    const stats = useMemo(() => {
        const total = proveedoresDB.length;
        const deuda = proveedoresDB.reduce((acc, p) => acc + (Number(p.saldo_pendiente) || 0), 0);
        return { total, deuda };
    }, [proveedoresDB]);

    // FILTRADO
    const proveedoresFiltrados = useMemo(() => {
        return proveedoresDB.filter(p =>
            p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
            p.nit?.includes(busqueda)
        );
    }, [proveedoresDB, busqueda]);

    // HANDLERS PROVEEDOR
    const handleOpenModal = (record = null) => {
        if (record) {
            setEditingProveedor(record);
            form.setFieldsValue(record);
        } else {
            setEditingProveedor(null);
            form.resetFields();
        }
        setIsModalOpen(true);
    };

    const onFinishProveedor = (values) => {
        const payload = { ...values, id: editingProveedor?.id };
        saveProveedor.mutate(payload, {
            onSuccess: () => {
                message.success(editingProveedor ? "Proveedor actualizado" : "Proveedor creado");
                setIsModalOpen(false);
                form.resetFields();
            },
            onError: (err) => message.error(err.message)
        });
    };

    const handleDeleteProveedor = (id) => {
        deleteProveedor.mutate(id, {
            onSuccess: () => message.success("Proveedor eliminado correctamente"),
            onError: (err) => message.error(err.message)
        });
    };

    const handleOpenPagoModal = (record) => {
        setProveedorAPagar(record);
        setTipoPago('total');
        formPago.resetFields();
        formPago.setFieldsValue({
            monto: Number(record.saldo_pendiente),
            metodo_pago: 'Transferencia'
        });
        setIsPagoModalOpen(true);
    };

    // 4. HANDLER PARA ABRIR EL HISTORIAL
    const handleOpenHistorial = (record) => {
        setProveedorSeleccionado(record);
        setIsHistorialModalOpen(true);
    };

    const onFinishPago = (values) => {
        if (values.metodo_pago === 'Efectivo Caja' && !cajaActiva) {
            return message.error("No puedes registrar un pago en efectivo porque no hay una caja abierta.");
        }

        const payload = {
            proveedor_id: proveedorAPagar.id,
            monto: values.monto,
            metodo_pago: values.metodo_pago,
            sesion_caja_id: values.metodo_pago === 'Efectivo Caja' ? cajaActiva?.id : null,
            numero_referencia: values.numero_referencia || null,
            notas: values.notas || ""
        };

        savePago.mutate(payload, {
            onSuccess: () => {
                message.success(`Pago de $${values.monto.toLocaleString('es-CO')} registrado con éxito.`);
                setIsPagoModalOpen(false);
            },
            onError: (err) => {
                message.error(err.message || "Error al registrar el pago.");
            }
        });
    };

    // 5. COLUMNAS DEL HISTORIAL SIN LA IMAGEN
    const columnasHistorial = [
        {
            title: 'Fecha',
            dataIndex: 'fecha',
            render: (text) => dayjs(text).format('DD/MMM/YYYY HH:mm')
        },
        {
            title: 'Método',
            dataIndex: 'metodo_pago',
            render: (metodo) => <Tag color={metodo.includes('Efectivo') ? 'warning' : 'blue'}>{metodo}</Tag>
        },
        {
            title: 'Monto Abonado',
            dataIndex: 'monto',
            align: 'right',
            render: (val) => <Text strong style={{ color: '#10b981' }}>${Number(val).toLocaleString('es-CO')}</Text>
        },
        {
            title: 'Referencia',
            dataIndex: 'numero_referencia',
            render: (text) => text || '---'
        }
    ];

    const columnasWeb = [
        {
            title: 'PROVEEDOR',
            dataIndex: 'nombre',
            render: (text, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong style={{ color: '#0f172a' }}>{text?.toUpperCase()}</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>NIT: {r.nit || 'N/A'}</Text>
                </Space>
            )
        },
        {
            title: 'CONTACTO',
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: '13px' }}><PhoneOutlined /> {r.telefono || '---'}</Text>
                    <Text style={{ fontSize: '12px' }} type="secondary"><MailOutlined /> {r.email || '---'}</Text>
                </Space>
            )
        },
        {
            title: 'SALDO PENDIENTE',
            dataIndex: 'saldo_pendiente',
            align: 'right',
            render: (val) => (
                <Text strong style={{ color: val > 0 ? '#ef4444' : '#16a34a' }}>
                    $ {Number(val || 0).toLocaleString('es-CO')}
                </Text>
            )
        },
        {
            title: 'ACCIONES',
            align: 'center',
            render: (_, record) => (
                <Space>
                    {/* BOTÓN DE HISTORIAL EN PC */}
                    <Tooltip title="Historial de Pagos">
                        <Button
                            type="default"
                            icon={<HistoryOutlined />}
                            style={{ color: '#8b5cf6', borderColor: '#e6d8ff', background: '#f5f3ff' }}
                            onClick={() => handleOpenHistorial(record)}
                        />
                    </Tooltip>

                    <Button 
                        type="primary" 
                        ghost 
                        icon={<EditOutlined />} 
                        onClick={() => handleOpenModal(record)} 
                        disabled={ !hasPermission('proveedores:editar') ? true : false } 
                        title={ !hasPermission('proveedores:editar') ? 'No tienes permiso para esta acción' : 'Editar proveedor' }
                    />

                    <Tooltip title={record.saldo_pendiente > 0 ? "No puedes eliminar un proveedor con deuda" : "Eliminar"}>
                        <Popconfirm
                            title="¿Eliminar este proveedor?"
                            description="Esta acción no se puede deshacer."
                            onConfirm={() => handleDeleteProveedor(record.id)}
                            okText="Sí, eliminar"
                            cancelText="Cancelar"
                            okButtonProps={{ danger: true, loading: deleteProveedor.isLoading }}
                            disabled={Number(record.saldo_pendiente) > 0}
                        >
                            <Button 
                                danger 
                                icon={<DeleteOutlined />}  
                                disabled={ !hasPermission('proveedores:eliminar') ? true : Number(record.saldo_pendiente) > 0 } 
                                title={ !hasPermission('proveedores:eliminar') ? 'No tienes permiso para esta acción' : 'Eliminar proveedor' }
                            />
                        </Popconfirm>
                    </Tooltip>

                    <Tooltip title="Abonar / Pagar Deuda">
                        <Button
                            type="primary"
                            icon={<DollarOutlined />}
                            disabled={Number(record.saldo_pendiente) <= 0}
                            style={{ background: Number(record.saldo_pendiente) > 0 ? '#10b981' : '#d9d9d9', borderColor: 'transparent' }}
                            onClick={() => handleOpenPagoModal(record)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <LayoutHome pageSeleccionada="proveedores">
            <div style={{ padding: isMobile ? '10px' : '20px', background: '#fff', width: '100%', minHeight: '100vh' }}>

                {/* HEADER DINÁMICO */}
                <div style={{ marginBottom: '25px' }}>
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '15px' }}>
                        <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Directorio de Proveedores</Title>

                        <div style={{ background: '#f8fafc', padding: isMobile ? '16px' : '12px 20px', borderRadius: '16px', border: '1px solid #e2e8f0', width: isMobile ? '100%' : 'auto' }}>
                            {isMobile ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>TOTAL ADEUDADO</Text>
                                        <Text strong style={{ fontSize: '20px', color: '#ef4444' }}>
                                            $ {stats.deuda.toLocaleString('es-CO')}
                                        </Text>
                                    </div>
                                    <Divider style={{ margin: '0' }} />
                                    <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>TOTAL REFS</Text>
                                            <Text strong style={{ fontSize: '14px' }}>{stats.total}</Text>
                                        </div>
                                        <div style={{ flex: 1, borderLeft: '1px solid #e2e8f0' }}>
                                            <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>CON DEUDA</Text>
                                            <Text strong style={{ fontSize: '14px', color: '#ef4444' }}>{proveedoresDB.filter(p => p.saldo_pendiente > 0).length}</Text>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
                                    <Space direction="vertical" size={0} align="center">
                                        <Text type="secondary" style={{ fontSize: '10px' }}>PROVEEDORES REGISTRADOS</Text>
                                        <Text strong style={{ fontSize: '18px' }}>{stats.total}</Text>
                                    </Space>
                                    <Divider type="vertical" style={{ height: '30px' }} />
                                    <Space direction="vertical" size={0} align="center">
                                        <Text type="secondary" style={{ fontSize: '10px' }}>TOTAL POR PAGAR</Text>
                                        <Text strong style={{ fontSize: '18px', color: '#ef4444' }}>$ {stats.deuda.toLocaleString('es-CO')}</Text>
                                    </Space>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* FILTROS Y ACCIÓN */}
                <Row gutter={[12, 12]} style={{ marginBottom: '25px' }}>
                    <Col xs={24} md={16}>
                        <Input
                            placeholder="Buscar por nombre o NIT..."
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            style={{ height: '45px', borderRadius: '12px' }}
                            value={busqueda}
                            allowClear
                            onChange={e => setBusqueda(e.target.value)}
                        />
                    </Col>
                    <Col xs={24} md={8}>
                        <Button
                            type="primary" block icon={<PlusOutlined />}
                            style={{ height: '45px', borderRadius: '12px', background: '#22BABB', fontWeight: 'bold' }}
                            onClick={() => handleOpenModal()}
                            disabled={ !hasPermission('proveedores:crear') ? true : false } 
                            title={ !hasPermission('proveedores:crear') ? 'No tienes permiso para esta acción' : 'Crear proveedor' }
                        >
                            NUEVO PROVEEDOR
                        </Button>
                    </Col>
                </Row>

                {/* LISTADO */}
                {isMobile ? (
                    <List
                        dataSource={proveedoresFiltrados}
                        loading={isLoading || isFetching}
                        renderItem={p => (
                            <Card style={{ marginBottom: '12px', borderRadius: '16px', border: '1px solid #f1f5f9' }} styles={{ body: { padding: '16px' } }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <Text strong style={{ fontSize: '15px' }}>{p.nombre?.toUpperCase()}</Text>
                                        <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>NIT: {p.nit || '---'}</Text>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <Text strong style={{ color: p.saldo_pendiente > 0 ? '#ef4444' : '#16a34a' }}>
                                            $ {Number(p.saldo_pendiente || 0).toLocaleString('es-CO')}
                                        </Text>
                                        {p.saldo_pendiente > 0 && <Tag color="error" style={{ display: 'block', marginRight: 0, marginTop: '4px' }}>DEUDA</Tag>}
                                    </div>
                                </div>
                                <Divider style={{ margin: '12px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Space direction="vertical" size={0}>
                                        <Text style={{ fontSize: '12px' }}><PhoneOutlined /> {p.telefono || '---'}</Text>
                                        <Text style={{ fontSize: '12px' }}><MailOutlined /> {p.email || '---'}</Text>
                                    </Space>
                                    <Space>
                                        {/* BOTÓN DE HISTORIAL EN MÓVIL */}
                                        <Button
                                            type="default"
                                            icon={<HistoryOutlined />}
                                            style={{ color: '#8b5cf6', borderColor: '#e6d8ff', background: '#f5f3ff' }}
                                            onClick={() => handleOpenHistorial(p)}
                                        />
                                        <Popconfirm
                                            title="¿Eliminar?"
                                            onConfirm={() => handleDeleteProveedor(p.id)}
                                            okText="Sí" cancelText="No"
                                            okButtonProps={{ danger: true, loading: deleteProveedor.isLoading }}
                                            disabled={Number(p.saldo_pendiente) > 0}
                                        >
                                            <Button danger icon={<DeleteOutlined />} disabled={Number(p.saldo_pendiente) > 0} />
                                        </Popconfirm>
                                        <Button type="primary" ghost icon={<EditOutlined />} onClick={() => handleOpenModal(p)} />
                                        {p.saldo_pendiente > 0 && (
                                            <Button type="primary" icon={<DollarOutlined />} style={{ background: '#10b981', borderColor: '#10b981' }} onClick={() => handleOpenPagoModal(p)} />
                                        )}
                                    </Space>
                                </div>
                            </Card>
                        )}
                    />
                ) : (
                    <Table
                        columns={columnasWeb}
                        dataSource={proveedoresFiltrados}
                        loading={isLoading || isFetching}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        style={{ border: '1px solid #f0f0f0', borderRadius: '12px', overflow: 'hidden' }}
                    />
                )}
            </div>

            {/* MODAL HISTORIAL DE PAGOS */}
            <Modal title={null} open={isHistorialModalOpen} onCancel={() => setIsHistorialModalOpen(false)} footer={<Button type="primary" onClick={() => setIsHistorialModalOpen(false)} style={{ background: '#22BABB' }}>Cerrar</Button>} centered width={700}>
                <Space style={{ marginBottom: '20px' }}>
                    <HistoryOutlined style={{ fontSize: '24px', color: '#8b5cf6' }} />
                    <Title level={4} style={{ margin: 0 }}>Historial de Pagos</Title>
                </Space>
                
                <div style={{ marginBottom: '20px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <Text strong style={{ fontSize: '16px' }}>{proveedorSeleccionado?.nombre}</Text>
                    <br />
                    <Text type="secondary">Deuda Actual: </Text>
                    <Text strong style={{ color: proveedorSeleccionado?.saldo_pendiente > 0 ? '#ef4444' : '#16a34a' }}>
                        ${Number(proveedorSeleccionado?.saldo_pendiente || 0).toLocaleString('es-CO')}
                    </Text>
                </div>

                <Table 
                    columns={columnasHistorial} 
                    dataSource={historialPagos} 
                    rowKey="id" 
                    size="small" 
                    loading={cargandoHistorial}
                    pagination={{ pageSize: 5 }}
                    locale={{ emptyText: 'No hay pagos registrados para este proveedor.' }}
                    scroll={{ x: 500 }}
                />
            </Modal>

            {/* MODAL PROVEEDOR */}
            <Modal title={null} open={isModalOpen} onCancel={() => setIsModalOpen(false)} footer={null} centered width={550} styles={{ body: { padding: '30px' } }}>
                <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                    <div style={{ background: '#f0fdfa', width: '55px', height: '55px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px' }}>
                        <TeamOutlined style={{ fontSize: '24px', color: '#22BABB' }} />
                    </div>
                    <Title level={4} style={{ margin: 0 }}>{editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}</Title>
                    <Text type="secondary">Completa la información para el directorio.</Text>
                </div>

                <Form form={form} layout="vertical" onFinish={onFinishProveedor}>
                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 14}>
                            <Form.Item name="nombre" label={<Text strong>Nombre o Razón Social</Text>} rules={[{ required: true, message: 'Requerido' }]}>
                                <Input placeholder="Ej: Distribuidora Sol" allowClear style={{ borderRadius: '8px' }} />
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 10}>
                            <Form.Item name="nit" label={<Text strong>NIT / Cédula</Text>}>
                                <Input placeholder="900.xxx.xxx-x" allowClear style={{ borderRadius: '8px' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item name="telefono" label={<Text strong>Teléfono</Text>} rules={[{ required: true, message: 'Requerido' }]}>
                                <Input prefix={<PhoneOutlined />} allowClear style={{ borderRadius: '8px' }} />
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item name="email" label={<Text strong>Correo</Text>}>
                                <Input prefix={<MailOutlined />} allowClear style={{ borderRadius: '8px' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="direccion" label={<Text strong>Dirección</Text>}>
                        <Input prefix={<EnvironmentOutlined />} allowClear placeholder="Ej: Calle 10 # 50-20" style={{ borderRadius: '8px' }} />
                    </Form.Item>

                    <Divider style={{ margin: '12px 0' }}><Text type="secondary" style={{ fontSize: '11px' }}>CONDICIONES COMERCIALES</Text></Divider>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="cupo_credito" label={<Text strong>Cupo de Crédito ($)</Text>}>
                                <InputNumber controls={false} style={{ width: '100%', borderRadius: '8px' }} min={0} placeholder="Ej: 5000000" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="plazo_pago" label={<Text strong>Plazo Pago (Días)</Text>}>
                                <InputNumber controls={false} style={{ width: '100%', borderRadius: '8px' }} min={0} placeholder="Ej: 15" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Button type="primary" htmlType="submit" block size="large" loading={saveProveedor.isLoading} style={{ background: '#22BABB', height: '50px', borderRadius: '12px', fontWeight: 'bold', marginTop: '10px' }} icon={<CheckCircleOutlined />}>
                        {editingProveedor ? 'ACTUALIZAR DATOS' : 'GUARDAR PROVEEDOR'}
                    </Button>
                </Form>
            </Modal>

            {/* MODAL REGISTRAR PAGO (TOTALMENTE REDISEÑADO) */}
            <Modal title={null} open={isPagoModalOpen} onCancel={() => setIsPagoModalOpen(false)} footer={null} centered width={450} styles={{ body: { padding: '30px' } }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ background: '#ecfdf5', width: '55px', height: '55px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px' }}>
                        <WalletOutlined style={{ fontSize: '24px', color: '#10b981' }} />
                    </div>
                    <Title level={4} style={{ margin: 0 }}>Registrar Pago</Title>
                    <Text type="secondary">{proveedorAPagar?.nombre}</Text>
                    <div style={{ marginTop: '10px', padding: '10px', background: '#fef2f2', borderRadius: '8px', border: '1px dashed #fca5a5' }}>
                        <Text strong style={{ color: '#ef4444' }}>Deuda Total: ${Number(proveedorAPagar?.saldo_pendiente || 0).toLocaleString('es-CO')}</Text>
                    </div>
                </div>

                {/* BOTONES RÁPIDOS ABONO VS TOTAL */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <Radio.Group
                        value={tipoPago}
                        onChange={(e) => {
                            setTipoPago(e.target.value);
                            if (e.target.value === 'total') {
                                formPago.setFieldsValue({ monto: Number(proveedorAPagar?.saldo_pendiente) });
                            } else {
                                formPago.setFieldsValue({ monto: null });
                            }
                        }}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        <Radio.Button value="parcial" style={{ width: '130px', textAlign: 'center' }}>Abono Parcial</Radio.Button>
                        <Radio.Button value="total" style={{ width: '130px', textAlign: 'center' }}>Saldar Deuda</Radio.Button>
                    </Radio.Group>
                </div>

                <Form form={formPago} layout="vertical" onFinish={onFinishPago}>
                    <Form.Item name="monto" label={<Text strong>Monto a Pagar</Text>} rules={[{ required: true, message: 'Ingresa el monto' }]}>
                        <InputNumber
                            style={{ width: '100%', borderRadius: '8px' }}
                            size="large"
                            min={1}
                            max={Number(proveedorAPagar?.saldo_pendiente || 0)}
                            disabled={tipoPago === 'total'} // Se bloquea si seleccionaron pagar todo
                            formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item name="metodo_pago" label={<Text strong>¿De dónde sale el dinero?</Text>} rules={[{ required: true, message: 'Selecciona método' }]}>
                        <Select size="large" style={{ borderRadius: '8px' }} placeholder="Seleccione método" popupMatchSelectWidth={false}>
                            {/* AQUÍ BLOQUEAMOS LA OPCIÓN SI cajaActiva ES NULL */}
                            <Select.Option value="Efectivo Caja" disabled={!cajaActiva}>
                                <Tag color={cajaActiva ? "warning" : "default"}>CAJA FÍSICA</Tag>
                                {cajaActiva ? 'Sacar de ESTA caja' : 'Caja Cerrada'}
                            </Select.Option>
                            <Select.Option value="Transferencia">
                                <Tag color="blue">BANCOS</Tag> Electrónico / Tarjeta
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="numero_referencia" label={<Text strong>N° de Comprobante / Referencia</Text>}>
                        <Input placeholder="Ej: Transacción #98273645" style={{ borderRadius: '8px', height: '40px' }} />
                    </Form.Item>

                    <Form.Item name="notas" label={<Text strong>Notas</Text>}>
                        <Input.TextArea rows={2} placeholder="Detalles extra..." style={{ borderRadius: '8px' }} />
                    </Form.Item>

                    <Button type="primary" htmlType="submit" block size="large" loading={savePago.isLoading} style={{ background: '#10b981', height: '50px', borderRadius: '12px', fontWeight: 'bold', border: 'none' }}>
                        CONFIRMAR PAGO
                    </Button>
                </Form>
            </Modal>

        </LayoutHome>
    );
};

export default Proveedores;